import { t } from "i18next";
import { useEffect, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { Mesocycle } from "../../../../interfaces/planning";

interface MesocycleModalProps {
    show: boolean;
    selectedWeeks: string[];
    editingMesocycle?: Mesocycle | null;
    onSave: (name: string, color: string, primaryObjective: string, mesocycleObjectives: string) => void;
    onClose: () => void;
}

export function MesocycleModal(props: MesocycleModalProps) {
    const [name, setName] = useState('');
    const [color, setColor] = useState('#ffffcc');
    const [primaryObjective, setPrimaryObjective] = useState('');
    const [mesocycleObjectives, setMesocycleObjectives] = useState('');

    // Update form when editing a mesocycle
    useEffect(() => {
        if (props.editingMesocycle) {
            setName(props.editingMesocycle.name);
            setColor(props.editingMesocycle.color || '#ffffcc');
            setPrimaryObjective(props.editingMesocycle.primary_objective || '');
            setMesocycleObjectives(props.editingMesocycle.mesocycle_objectives || '');
        } else {
            setName('');
            setColor('#ffffcc');
            setPrimaryObjective('');
            setMesocycleObjectives('');
        }
    }, [props.editingMesocycle, props.show]);

    const handleSave = (event: any) => {
        event.preventDefault();
        if (name.trim() === '') {
            alert(t("Please enter a name for the mesocycle"));
            return;
        }
        props.onSave(name, color, primaryObjective, mesocycleObjectives);
        setName('');
        setColor('#ffffcc');
        setPrimaryObjective('');
        setMesocycleObjectives('');
    }

    const handleClose = () => {
        setName('');
        setColor('#ffffcc');
        setPrimaryObjective('');
        setMesocycleObjectives('');
        props.onClose();
    }

    const weeks = props.editingMesocycle ? props.editingMesocycle.weeks : props.selectedWeeks;
    const isEditing = !!props.editingMesocycle;

    return (
        <Modal show={props.show} onHide={handleClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>{isEditing ? t("Edit Mesocycle") : t("Define Mesocycle")}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form onSubmit={handleSave}>
                    <Form.Group className="mb-3">
                        <Form.Label>{t("Selected weeks")}: {weeks.length}</Form.Label>
                        <div className="text-muted small">
                            {weeks.join(', ')}
                        </div>
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="mesocycleName">
                        <Form.Label>{t("Mesocycle name")}</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder={t("e.g., Accumulation")}
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                        />
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="mesocycleColor">
                        <Form.Label>{t("Background color")}</Form.Label>
                        <div className="d-flex align-items-center gap-2">
                            <Form.Control
                                type="color"
                                value={color}
                                onChange={(e) => setColor(e.target.value)}
                                style={{ width: '60px', height: '40px' }}
                            />
                            <Form.Control
                                type="text"
                                value={color}
                                onChange={(e) => setColor(e.target.value)}
                                placeholder="#ffffcc"
                            />
                        </div>
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="primaryObjective">
                        <Form.Label>{t("Primary Objective")}</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={2}
                            placeholder={t("e.g., Build aerobic base")}
                            value={primaryObjective}
                            onChange={(e) => setPrimaryObjective(e.target.value)}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="mesocycleObjectives">
                        <Form.Label>{t("Mesocycle Objectives")}</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            placeholder={t("e.g., Increase volume, technique improvement")}
                            value={mesocycleObjectives}
                            onChange={(e) => setMesocycleObjectives(e.target.value)}
                        />
                    </Form.Group>

                    <div className="d-flex justify-content-end gap-2">
                        <Button variant="secondary" onClick={handleClose}>
                            {t("Cancel")}
                        </Button>
                        <Button variant="primary" type="submit">
                            {t("Save")}
                        </Button>
                    </div>
                </Form>
            </Modal.Body>
        </Modal>
    );
}
